import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  commonButtonProps,
  commonTextFieldProps,
  tableCellStyles,
  tableHeaderStyles,
} from "../../../utils/styles";
import { routeServiceApi } from "./apiService";
import { useToast } from "../../../components/Toast";

interface AddScheduleModalProps {
  open: boolean;
  onClose: () => void;
  route: any;
  onSuccess?: () => void;
}

interface TimingData {
  scheduleId: string;
  stopId: string;
  stopTime: string;
  createdBy: string;
}

const AddScheduleModal: React.FC<AddScheduleModalProps> = ({
  open,
  onClose,
  route,
  onSuccess,
}) => {
  const { showToast } = useToast();

  const [timingsData, setTimingsData] = useState<TimingData[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize timings data when modal opens
  React.useEffect(() => {
    if (open && route && route.stops) {
      const initialTimings = route.stops.map((stop: any) => ({
        scheduleId: "", // Will be set after schedule creation
        stopId: stop.id,
        stopTime: "",
      }));
      setTimingsData(initialTimings);
    }
  }, [open, route]);

  const handleTimeChange = (stopId: string, newTime: string) => {
    setTimingsData((prev) =>
      prev.map((timing) =>
        timing.stopId === stopId ? { ...timing, stopTime: newTime } : timing
      )
    );
  };

  const handleCancel = () => {
    setTimingsData([]);
    setIsSubmitting(false);
    onClose();
  };

  const handleSaveSchedule = async () => {
    try {
      setIsSubmitting(true);

      // Validate that all stops have times
      const hasEmptyTimes = timingsData.some((timing) => !timing.stopTime);
      if (hasEmptyTimes) {
        showToast("Please set times for all stops", "error");
        return;
      }

      // Create schedule data
      const scheduleData = {
        routeId: route.id,
        name: `${route.fromLocation} to ${route.toLocation} Schedule`,
        status: "ACTIVE",
        fromLocation: route.fromLocation,
        toLocation: route.toLocation,
        stops: timingsData,
      };

      const response = await routeServiceApi.createSchedule(
        route.id,
        scheduleData
      );

      if (response) {
        showToast("Schedule created successfully", "success");
        setTimingsData([]);
        setIsSubmitting(false);
        onClose();
        onSuccess?.();
      }
    } catch (error) {
      console.error("Error creating schedule:", error);
      showToast("Failed to create schedule", "error");
      setIsSubmitting(false);
    }
  };

  if (!route || !route.stops) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        Add New Schedule - {route.fromLocation} to {route.toLocation}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Set arrival times for each stop in this schedule
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...tableHeaderStyles }}>Stop Number</TableCell>
                <TableCell sx={{ ...tableHeaderStyles }}>Stop Name</TableCell>
                <TableCell sx={{ ...tableHeaderStyles }}>Stop Time</TableCell>
                <TableCell sx={{ ...tableHeaderStyles }}>
                  Contact Person
                </TableCell>
                <TableCell sx={{ ...tableHeaderStyles }}>
                  Contact Number
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {route.stops
                .sort((a: any, b: any) => a.stopNumber - b.stopNumber)
                .map((stop: any, index: any) => (
                  <TableRow key={stop.id}>
                    <TableCell sx={tableCellStyles}>
                      {stop.stopNumber}
                    </TableCell>
                    <TableCell sx={tableCellStyles}>{stop.stopName}</TableCell>
                    <TableCell sx={tableCellStyles}>
                      <TextField
                        type="time"
                        value={
                          timingsData.find(
                            (timing) => timing.stopId === stop.id
                          )?.stopTime || ""
                        }
                        onChange={(e) =>
                          handleTimeChange(stop.id, e.target.value)
                        }
                        {...commonTextFieldProps}
                        required
                      />
                    </TableCell>
                    <TableCell sx={tableCellStyles}>
                      {stop.contactPersonName || "-"}
                    </TableCell>
                    <TableCell sx={tableCellStyles}>
                      {stop.contactPersonNumber || "-"}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancel}
          color="error"
          variant="outlined"
          {...commonButtonProps}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSaveSchedule}
          variant="contained"
          color="primary"
          {...commonButtonProps}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Creating..." : "Create Schedule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddScheduleModal;
